import { forwardRef, type PropsWithChildren, useMemo } from 'react'

import { Loader } from '../Loader'
import resolveTailwindClasses from '../utils/resolveTailwindClasses'
import { ButtonColor, type ButtonProps, ButtonShape, ButtonSize } from './types'

const Button = forwardRef<HTMLButtonElement, PropsWithChildren<ButtonProps>>(
  (
    {
      children,
      block = false,
      iconOnlyNotSquare = false,
      LeadingIcon,
      leadingIconClassName,
      TrailingIcon,
      trailingIconClassName,
      size = ButtonSize.sm,
      color = ButtonColor.primary,
      shape = ButtonShape.rounded,
      isBlurEffect,
      type = 'button',
      loading,
      ...props
    },
    ref
  ) => {
    const sizeClasses = useMemo(() => {
      const isIconOnly = !children && ((LeadingIcon && !TrailingIcon) || (!LeadingIcon && TrailingIcon))
      const iconOnlySquare = isIconOnly && (shape === ButtonShape.circle || !iconOnlyNotSquare)

      switch (size) {
        case ButtonSize.xxs: {
          const minSize = `min-h-5${iconOnlySquare ? ' min-w-5' : ''}`
          const padding = `p-[3px]${isIconOnly ? ' text-[0] px-0.5' : ''}`

          return `text-xs leading-4 ${padding} ${minSize}`
        }
        case ButtonSize.editor: {
          return 'text-sm leading-5 p-[3px]'
        }
        case ButtonSize.xs: {
          const minSize = `min-h-8${iconOnlySquare ? ' min-w-8' : ''}`
          const padding = `p-[5px]${isIconOnly ? ' text-[0] px-0.5' : ''}`

          return `text-sm leading-5 ${padding} ${minSize}`
        }
        case ButtonSize.sm: {
          const minSize = `min-h-9${iconOnlySquare ? ' min-w-9' : ''}`
          const padding = `py-[7px] ${isIconOnly ? 'text-[0] px-0.5' : 'px-[11px]'}`

          return `text-sm leading-5 ${padding} ${minSize}`
        }
        case ButtonSize.md: {
          const minSize = `min-h-10${iconOnlySquare ? ' min-w-10' : ''}`
          const padding = `py-[9px] ${isIconOnly ? 'text-[0] px-0.5' : 'px-[13px]'}`

          return `text-sm leading-5 ${padding} ${minSize}`
        }
        case ButtonSize.lg: {
          const minSize = `min-h-11${iconOnlySquare ? ' min-w-11' : ''}`
          const padding = `py-[11px] ${isIconOnly ? 'text-[0] px-0.5' : 'px-[15px]'}`

          return `text-base leading-6 ${padding} ${minSize}`
        }
        case ButtonSize.xl: {
          const minSize = `min-h-12${iconOnlySquare ? ' min-w-12' : ''}`
          const padding = `py-[13px] ${isIconOnly ? 'text-[0] px-0.5' : 'px-[17px]'}`

          return `text-base leading-6 ${padding} ${minSize}`
        }
        case ButtonSize.inline: {
          return ''
        }
        default:
          console.warn(`Unknown button size: ${size}`)

          return ''
      }
    }, [size])

    const colorClasses = useMemo(() => {
      switch (color) {
        case ButtonColor.editor:
          return 'border-transparent bg-transparent text-primary-200 hover:text-white hover:bg-primary-600 focus-visible:bg-primary-900 active:text-white active:border-primary-500 disabled:border-transparent disabled:text-primary-200/50 disabled:hover:bg-transparent'
        case ButtonColor.editorDark:
          return 'border-primary-600 bg-primary-900 text-primary-300 hover:border-primary-400'
        case ButtonColor.danger:
          return 'border-error-600 bg-error-600 hover:bg-error-500 focus-visible:bg-error-600/50 active:border-error-300'
        case ButtonColor.purple:
          return 'border-transparent bg-primary-500 text-white hover:bg-primary-600 focus-visible:bg-primary-700 active:border-primary-500'
        case ButtonColor.primary:
          return 'bg-primary-500 border-white/10 text-neutral-100 hover:bg-primary-400 disabled:bg-[#443A92] disabled:text-[#DEDEF9]/50'
        case ButtonColor.secondary:
          return 'bg-transparent bg-primary-1000 border-primary-700 text-neutral-100 hover:bg-primary-1000/90 hover:border-primary-400 disabled:bg-[#443A92] disabled:text-[#DEDEF9]/50 disabled:hover:border-primary-500'
        case ButtonColor.tertiary:
          return 'border-transparent text-neutral-100 hover:bg-white/10 disabled:text-[#DEDEF9]/50'
        case ButtonColor.quaternary:
          return 'bg-transparent border-transparent'
        case ButtonColor.quinary:
          return 'bg-[#3A307B] border-primary-400 text-neutral-100 hover:bg-[#3A307B]/80 disabled:bg-[#443A92] disabled:text-[#DEDEF9]/50'
        default:
          // In a library, avoid environment-specific checks. Consider logging a warning instead.
          console.warn(`Unknown button color: ${color}`)

          return ''
      }
    }, [color])

    const shapeClasses = useMemo(() => {
      switch (shape) {
        case ButtonShape.circle:
          return 'rounded-full'
        case ButtonShape.rounded:
          return 'rounded-lg'
        default:
          console.warn(`Unknown button shape: ${shape}`)

          return ''
      }
    }, [shape])

    const layoutClasses = useMemo(() => {
      const result: string[] = []

      if (block) {
        result.push('flex')
      } else {
        result.push('inline-flex')
      }

      if (LeadingIcon || TrailingIcon) {
        switch (size) {
          case ButtonSize.xxs:
          case ButtonSize.editor:
          case ButtonSize.xs:
          case ButtonSize.sm:
          case ButtonSize.md:
            result.push('gap-1')
            break
          case ButtonSize.lg:
          case ButtonSize.xl:
            result.push('gap-1.5')
            break
          default:
            break
        }
      }

      return result
    }, [block, size, LeadingIcon, TrailingIcon])

    const iconColorClasses = useMemo(() => {
      switch (color) {
        case ButtonColor.editor:
        case ButtonColor.editorDark:
        case ButtonColor.danger:
        case ButtonColor.purple:
          return ''
        case ButtonColor.primary:
        case ButtonColor.secondary:
        case ButtonColor.tertiary:
        case ButtonColor.quaternary:
          return 'text-primary-300 [:disabled>&]:text-primary-300/40'
        default:
          return ''
      }
    }, [color])

    const iconSizeClasses = useMemo(() => {
      switch (size) {
        case ButtonSize.xxs:
          return 'size-3 min-w-3'
        case ButtonSize.editor:
        case ButtonSize.xs:
          return 'size-4 min-w-4'
        case ButtonSize.sm:
        case ButtonSize.md:
        case ButtonSize.inline:
          return 'size-[1.125rem] min-w-[1.125rem]'
        case ButtonSize.lg:
        case ButtonSize.xl:
          return 'size-5 min-w-5'
        default:
          return ''
      }
    }, [size])

    return (
      <button
        ref={ref}
        type={type}
        {...props}
        className={resolveTailwindClasses(
          'relative max-h-12 max-w-80 items-center justify-center overflow-hidden border font-semibold outline-none outline-offset-2 outline-transparent focus-visible:outline-1 focus-visible:outline-white disabled:cursor-not-allowed disabled:opacity-50',
          sizeClasses,
          colorClasses,
          shapeClasses,
          layoutClasses,
          props.className
        )}
      >
        {loading && <Loader className="mr-2" size="xs" />}

        {LeadingIcon && (
          <LeadingIcon className={resolveTailwindClasses(iconColorClasses, iconSizeClasses, leadingIconClassName)} />
        )}

        {children}

        {TrailingIcon && (
          <TrailingIcon className={resolveTailwindClasses(iconColorClasses, iconSizeClasses, trailingIconClassName)} />
        )}

        {isBlurEffect && <div className="absolute left-1/2 top-10 h-8 w-2/3 -translate-x-1/2 bg-primary-100 blur-lg" />}
      </button>
    )
  }
)

Button.displayName = 'Button'

export default Button
